import { StudioMemberRole } from "@prisma/client";

import type {
  ChannelContentType,
  ChannelReactionPermission,
  ChannelWritingPermission,
} from "@/lib/types/channel";

/**
 * Client-side mirror of the server predicates in
 * `truman/src/lambda/api/studio-trpc/lib/channel-permissions.ts`. The server is
 * the enforcement point (`posts.create`, `messages.send`, the reaction routes);
 * these exist so the UI offers exactly what the server would accept, instead of
 * gating on member role alone and ignoring the channel's own settings.
 *
 * Keep the two files in step — a divergence shows up either as a permission
 * that silently does nothing, or as a composer that 403s on submit.
 */

/** Broader than OWNER/ADMIN — coaches may post and react too. */
export const TEAM_ROLES: ReadonlySet<StudioMemberRole> = new Set([
  StudioMemberRole.OWNER,
  StudioMemberRole.ADMIN,
  StudioMemberRole.COACH,
  StudioMemberRole.GUEST_COACH,
]);

type MaybeRole = StudioMemberRole | null | undefined;

/** POSTS is the only content type the create-post composer can target. */
const POSTS_CONTENT_TYPE: ChannelContentType = "POSTS";

export function isTeamRole(role: MaybeRole): boolean {
  return !!role && TEAM_ROLES.has(role);
}

/**
 * Returns true if the member may create a post/message in this channel.
 * Team roles always pass; TEAM_MEMBERS_ONLY requires one.
 * A missing role (non-member / signed-out) always fails closed.
 */
export function canMemberPost(
  writingPermission: ChannelWritingPermission,
  memberRole: MaybeRole,
): boolean {
  if (!memberRole) return false;
  if (isTeamRole(memberRole)) return true;
  return writingPermission === "EVERYONE";
}

/**
 * True if at least one of these channels accepts a post from this member.
 * Drives the global header's create-post button, which would otherwise offer
 * members a composer with nowhere to submit to.
 */
export function canMemberPostInAnyChannel(
  channels: readonly {
    contentType: ChannelContentType;
    writingPermission: ChannelWritingPermission;
  }[],
  memberRole: MaybeRole,
): boolean {
  return channels.some(
    (channel) =>
      channel.contentType === POSTS_CONTENT_TYPE &&
      canMemberPost(channel.writingPermission, memberRole),
  );
}

/**
 * Returns true if the member may add/remove a reaction.
 * DISABLED blocks everyone (even admins); TEAM_MEMBERS_ONLY requires a team role.
 */
export function canMemberReact(
  reactionPermission: ChannelReactionPermission,
  memberRole: MaybeRole,
): boolean {
  if (reactionPermission === "DISABLED") return false;
  if (!memberRole) return false;
  if (reactionPermission === "TEAM_MEMBERS_ONLY") return isTeamRole(memberRole);
  return true;
}

/**
 * Reaction gate for a *post*, which may or may not belong to a channel.
 *
 * Mirrors `posts.toggleReaction`, which only consults the channel when the post
 * actually has one (`if (post.channelId && post.channel && ...)`) — so a
 * channel-less admin-feed post carries no channel restriction.
 *
 * A channel whose `reactionPermission` is absent is treated as EVERYONE rather
 * than blocked: the field is optional on `FullPost.channel`, and a query that
 * forgets to select it must not silently strip reactions from an ordinary
 * channel. The server still rejects anything it shouldn't allow, so the
 * permissive default costs a 403 at worst — whereas failing closed would break
 * the common case invisibly.
 */
export function canMemberReactToPost(
  channel:
    | { reactionPermission?: ChannelReactionPermission | null }
    | null
    | undefined,
  memberRole: MaybeRole,
): boolean {
  if (!memberRole) return false;
  if (!channel) return true;
  return canMemberReact(channel.reactionPermission ?? "EVERYONE", memberRole);
}
